import { Fragment, useState } from 'react';
import linkifyHtml from 'linkify-html';
import { useRouter } from 'next/router';

import Expander from '../../../components/Expander';
import { Box, Contain } from '../../../components/Grid';
import Image from '../../../components/Image';
import CurrentPromotionsSection from '../../../components/PartnerDetails/CurrentPromotionsSection';
import OutletSection from '../../../components/PartnerDetails/OutletSection';
import StoreOutletDialog from '../../../components/PartnerDetails/StoreOutletDialog';
import { Issuance, PartnerLayout } from '../../../components/PartnerDetails/styles';
import SeoHead from '../../../components/SeoHead';
import ShareButton from '../../../components/ShareButton';
import { getPartnerDetails } from '../../../services/api';
import lineBreaker from '../../../utils/lineBreaker';
import withErrorHandling from '../../../utils/withErrorHandling';
import Error404 from '../../404';

const renderIssuance = (linkpointDescription, linkpointRemark) => {
  return (
    <Issuance>
      <span
        data-testid="linkpointDescription"
        className="linkpointDescription"
        dangerouslySetInnerHTML={{
          __html: lineBreaker(
            linkifyHtml(
              linkpointDescription.replace(/~~([^~]+)~~/g, '<span class="highlight">$1</span>'),
            ),
          ),
        }}
      />
      {linkpointRemark && <small>{linkpointRemark}</small>}
    </Issuance>
  );
};

const PartnerDetail = ({ partnerDetails, error }) => {
  const [outletsDialogVisible, setOutletsDialogVisible] = useState(false);
  const Router = useRouter();

  if (error || partnerDetails.status === 0) {
    return <Error404 />;
  }

  const {
    imgUrls,
    urlInfo,
    name,
    description,
    tnc,
    stores,
    linkpointDescription,
    linkpointRemark,
    promotions,
    webInfo,
  } = { ...partnerDetails };

  const { metaTitle, metaDescription, story } = webInfo;
  const { web, facebook, instagram } = urlInfo;
  const { permalink } = Router.query;
  const shareUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/discover/partner/${permalink}`;

  const currentPromotions = promotions?.filter((x) => x.categoryIds?.length > 0);
  const linkpointText = linkpointDescription || linkpointRemark;

  const PartnerInfo = () => (
    <div className="merchant-info">
      {web && (
        <a href={web} target="_blank" rel="noreferrer noopener">
          <img alt="websiteUrl" src="/static/img/icons/website.png" />
        </a>
      )}
      {facebook && (
        <a href={facebook} target="_blank" rel="noreferrer noopener">
          <img alt="Facebook" src="/static/img/icons/facebook.png" />
        </a>
      )}
      {instagram && (
        <a href={instagram} target="_blank" rel="noreferrer noopener">
          <img alt="Instagram" src="/static/img/icons/instagram.png" />
        </a>
      )}
    </div>
  );

  return (
    <Contain>
      <SeoHead
        title={!metaTitle ? name : metaTitle}
        description={!metaDescription ? name : metaDescription}
        image={imgUrls.banner}
      />
      <PartnerLayout>
        <div className="merchant-meta">
          <div className="banner">
            <Image
              fill
              alt="banner image"
              src={imgUrls.banner}
              sizes="(max-width: 768px) 100vw"
              className="merchant-banner object-cover"
            />
            <img alt={name} className="logo" src={imgUrls.logo} />
          </div>

          {linkpointText && (
            <div className="issuance-holder">
              {renderIssuance(linkpointDescription, linkpointRemark)}
            </div>
          )}
          <Box className="hidden-mobile" style={{ borderBottom: '1px solid #ddd' }}>
            <PartnerInfo />
          </Box>
          <Box className="visible-mobile">
            <ShareButton shareUrl={shareUrl} title={name} />
          </Box>
          <Expander
            summary={`About ${name}`}
            active
            details={
              <Fragment>
                <div
                  dangerouslySetInnerHTML={{
                    __html: lineBreaker(linkifyHtml(description)),
                  }}
                />
                {web && (
                  <p>
                    Visit{' '}
                    <a href={web} target="_blank" rel="noreferrer noopener">
                      {web.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  </p>
                )}
                <Box className="visible-mobile" mt="md">
                  <PartnerInfo />
                </Box>
              </Fragment>
            }
          />
        </div>
        <div className="merchant-detail">
          <div className="merchant-detail-main">
            <div className="partnerpage-banner">
              <Image
                fill
                alt="banner image"
                src={imgUrls.banner}
                sizes="(max-width: 1024px) 100vw, 65vw"
              />
            </div>
            <ShareButton shareUrl={shareUrl} title={name} />
            {linkpointText && renderIssuance(linkpointDescription, linkpointRemark)}
          </div>
          {story && (
            <div className="lp-award">
              <Expander
                summary="Linkpoints Award"
                active
                details={<div dangerouslySetInnerHTML={{ __html: story }} />}
              />
            </div>
          )}
          {currentPromotions?.length > 0 && (
            <CurrentPromotionsSection currentPromotions={currentPromotions} />
          )}
          {tnc && (
            <div className="tnc">
              <Expander
                summary="Terms and Conditions"
                active
                isLast={stores?.length <= 0}
                details={
                  <p
                    dangerouslySetInnerHTML={{
                      __html: lineBreaker(linkifyHtml(tnc)),
                    }}
                  />
                }
              />
            </div>
          )}
          {stores?.length > 0 && (
            <OutletSection
              outlets={stores}
              handleOutletDialog={() => setOutletsDialogVisible(true)}
            />
          )}
        </div>
      </PartnerLayout>
      <StoreOutletDialog
        outlets={stores}
        outletsDialogVisible={outletsDialogVisible}
        handleStoreDialog={() => setOutletsDialogVisible(false)}
      />
    </Contain>
  );
};

const getPartnerData = async (ctx) => {
  const partnerDetails = await getPartnerDetails(ctx.params.permalink);
  return {
    props: {
      partnerDetails,
    },
  };
};

export const getServerSideProps = withErrorHandling(getPartnerData);

export default PartnerDetail;
